import type { Presentation } from '@magicdoor/magic-use-case-solid';
import IconEmptyDefault from '~/assets/icons/empty.svg';
import IconEmptyBlack from '~/assets/icons/emptyBlack.svg';
import IconEmptyGreen from '~/assets/icons/emptyGreen.svg';
import IconEmptyOrange from '~/assets/icons/emptyOrange.svg';
import IconEmptyPurple from '~/assets/icons/emptyPurple.svg';
import { TranslationKeys } from '~/hooks/useLocalization';
import type { MainAppState } from '~/state/mainAppState';
import type { CompanyInfo } from '~/use-cases/types/CompanyInfo';
import { CompanyTheme } from '~/use-cases/types/CompanyTheme';
import { presentCompanyName } from './companyName';
import type { PresentableCompanyStyle } from './types/PresentableCompanyStyle';

interface ThemeColors {
  primaryColor: string;
  hoverColor: string;
  activeColor: string;
  gradientStartColor: string;
  gradientEndColor: string;
}

const FALLBACK_COLORS: ThemeColors = {
  primaryColor: '#0C48BC',
  hoverColor: '#2b7fff',
  activeColor: '#0054d4',
  gradientStartColor: '#0C48BC',
  gradientEndColor: '#0065ff',
};

const THEME_COLORS: Record<CompanyTheme, ThemeColors> = {
  [CompanyTheme.Blue]: {
    primaryColor: '#0C48BC',
    hoverColor: '#3670E0',
    activeColor: '#073A9E',
    gradientStartColor: '#E7ECF6',
    gradientEndColor: '#A0B8E8',
  },
  [CompanyTheme.Purple]: {
    primaryColor: '#9211AF',
    hoverColor: '#AC37C6',
    activeColor: '#7B1192',
    gradientStartColor: '#F3E9F0',
    gradientEndColor: '#B973CB',
  },
  [CompanyTheme.Orange]: {
    primaryColor: '#B64F16',
    hoverColor: '#DF7337',
    activeColor: '#6D3500',
    gradientStartColor: '#F3EDE9',
    gradientEndColor: '#D4A073',
  },
  [CompanyTheme.Green]: {
    primaryColor: '#0F995E',
    hoverColor: '#24B476',
    activeColor: '#087546',
    gradientStartColor: '#E9F3EA',
    gradientEndColor: '#B0DFCB',
  },
  [CompanyTheme.Black]: {
    primaryColor: '#232231',
    hoverColor: '#4F4E67',
    activeColor: '#0D0C15',
    gradientStartColor: '#F3F3F3',
    gradientEndColor: '#9C9CB3',
  },
};

const THEME_EMPTY_ICONS: Record<CompanyTheme, string> = {
  [CompanyTheme.Blue]: IconEmptyDefault,
  [CompanyTheme.Purple]: IconEmptyPurple,
  [CompanyTheme.Orange]: IconEmptyOrange,
  [CompanyTheme.Green]: IconEmptyGreen,
  [CompanyTheme.Black]: IconEmptyBlack,
};

const colorsOf = (companyInfo?: CompanyInfo): ThemeColors =>
  companyInfo ? THEME_COLORS[companyInfo.theme ?? CompanyTheme.Blue] : FALLBACK_COLORS;

export const presentCompanyStyle: Presentation<MainAppState, PresentableCompanyStyle> = (state) => {
  const companyInfo = state.user.companyInfo;
  const name = companyInfo?.name;
  const approvedLegalNames = companyInfo?.approvedLegalNames ?? [];
  const isNameLegalEntity = !!name && approvedLegalNames.includes(name);
  return {
    ...colorsOf(companyInfo),
    name,
    logo: companyInfo?.logo,
    cover: companyInfo?.coverUrl,
    customDomain: companyInfo?.customDomain,
    email: companyInfo?.email,
    phoneNumber: companyInfo?.phoneNumber,
    facebook: companyInfo?.facebook,
    instagram: companyInfo?.instagram,
    linkedIn: companyInfo?.linkedIn,
    twitter: companyInfo?.twitter,
    tikTok: companyInfo?.tikTok,
    youtube: companyInfo?.youtube,
    metadataTitle: companyInfo?.metadataTitle,
    metadataDescription: companyInfo?.metadataDescription,
    metadataThemeColor: companyInfo?.metadataThemeColor,
    metadataTitleTemplate: companyInfo?.metadataTitleTemplate,
    isCompanyInfoLoaded: !!companyInfo,
    themeEmptyIcon: companyInfo && THEME_EMPTY_ICONS[companyInfo.theme ?? CompanyTheme.Blue],
    socialMedia: [
      companyInfo?.facebook,
      companyInfo?.instagram,
      companyInfo?.linkedIn,
      companyInfo?.twitter,
      companyInfo?.tikTok,
      companyInfo?.youtube,
    ].some((link) => !!link),
    iconUrl: companyInfo?.iconUrl,
    complianceIconRealtor: companyInfo?.complianceIcons?.realtor,
    complianceIconMls: companyInfo?.complianceIcons?.mls,
    equalHouseing: companyInfo?.equalHouseing,
    canReceiveRentalApplications: companyInfo?.canReceiveRentalApplications,
    translationLanguages: companyInfo?.translationLanguages,
    language: companyInfo?.language,
    leasingPhoneNumber: companyInfo?.leasingPhoneNumber,
    approvedLegalNames: companyInfo?.approvedLegalNames,
    listedLegalNames: isNameLegalEntity ? approvedLegalNames.filter((legalName) => legalName !== name) : approvedLegalNames,
    legalNamesIntro: {
      key: isNameLegalEntity ? TranslationKeys.DBA_ALSO_OPERATES_AS : TranslationKeys.DBA_TENANT_FRIENDLY_NAME,
      params: { companyName: presentCompanyName(state) },
    },
  };
};
